/**
 * @fileoverview Преобразование вложенных ответов сервисов в плоскую структуру
 * @description Улучшенная версия flattenRuntimeResponse с четкой архитектурой
 */

import {
  isDictionary,
  getLocalizedName,
  // normalizeValue, // Используется в других модулях
  isSimpleArray,
  normalizeSimpleArray,
} from "./responseNormalizer";
import { RESPONSE_DATA_TYPES, LOCALES } from "@/shared/mention/constants";

/**
 * Опции для flatten операции
 * @typedef {Object} FlattenOptions
 * @property {string} [locale] - Локаль для локализации
 * @property {string} [container] - Текущий контейнер (родительский путь)
 * @property {Array} [result] - Массив для накопления результатов
 * @property {string} [pathPrefix] - Префикс пути
 * @property {number} [level] - Уровень вложенности
 * @property {boolean} [isArrayItem] - Является ли элементом массива
 * @property {string[]} [matchedMentions] - Список совпадающих mention для выбора элементов массива
 */

/**
 * Преобразует вложенный объект в плоскую структуру (простой вариант для bulk insert)
 * @param {Object} obj - Объект для преобразования
 * @param {FlattenOptions} options - Опции преобразования
 * @returns {Array<{container: string, key: string, path: string, value: any}>} Плоский список полей
 */
export function flattenToSimpleList(
  obj,
  {
    locale = LOCALES.RUSSIAN,
    container = null,
    result = [],
    inArray = false,
  } = {}
) {
  if (!obj || typeof obj !== "object") {
    return result;
  }

  Object.entries(obj).forEach(([key, value]) => {
    const basePath = container ? `${container}_${key}` : key;

    const isObject = value && typeof value === "object";

    // СПРАВОЧНИК (не массив)
    if (isDictionary(value) && !inArray) {
      result.push({
        container,
        key,
        path: basePath,
        value: getLocalizedName(value, locale),
      });
      return;
    }

    // ОБЪЕКТ - рекурсивно обрабатываем
    if (isObject) {
      flattenToSimpleList(value, {
        locale,
        container: basePath,
        result,
        inArray,
      });
      return;
    }

    // ПРИМИТИВ - показываем даже пустые
    result.push({
      container,
      key,
      path: basePath,
      value: value ?? "",
    });
  });

  return result;
}

/**
 * Преобразует вложенный объект в структурированную иерархию
 * Для отображения в UI с возможностью разворачивания контейнеров
 * @param {Object} obj - Объект для преобразования
 * @param {FlattenOptions} options - Опции преобразования
 * @returns {Array} Структурированный список с иерархией
 */
export function flattenToStructuredList(
  obj,
  {
    locale = LOCALES.RUSSIAN,
    container = null,
    result = [],
    pathPrefix = "",
    level = 0,
    isArrayItem = false,
    matchedMentions = [],
  } = {}
) {
  if (!obj || typeof obj !== "object") {
    return result;
  }

  Object.entries(obj).forEach(([key, value]) => {
    let currentPath, fullPath;

    // Для элементов массива используем индекс как ключ
    if (isArrayItem && !isNaN(key)) {
      currentPath = pathPrefix ? `${pathPrefix}_${key}` : key;
      fullPath = container ? `${container}_${currentPath}` : currentPath;
    } else {
      currentPath = pathPrefix ? `${pathPrefix}_${key}` : key;
      fullPath = container ? `${container}_${currentPath}` : currentPath;
    }

    // МАССИВ
    if (Array.isArray(value)) {
      handleArray(
        key,
        value,
        fullPath,
        container,
        result,
        locale,
        level,
        matchedMentions
      );
      return;
    }

    const isObject = value && typeof value === "object";

    // СПРАВОЧНИК
    if (isDictionary(value)) {
      result.push({
        container,
        key,
        path: fullPath,
        value: getLocalizedName(value, locale),
        isExpandable: false,
        type: RESPONSE_DATA_TYPES.PRIMITIVE,
      });
      return;
    }

    // ОБЪЕКТ
    if (isObject) {
      const children = [];

      flattenToStructuredList(value, {
        locale,
        container: fullPath,
        result: children,
        pathPrefix: currentPath,
        level: level + 1,
        isArrayItem: false,
        matchedMentions,
      });

      if (children.length > 0) {
        result.push({
          container,
          key,
          path: fullPath,
          value: "",
          isExpandable: true,
          type: RESPONSE_DATA_TYPES.OBJECT,
          children,
          childrenCount: children.length,
        });
      } else {
        result.push({
          container,
          key,
          path: fullPath,
          value: "",
          isExpandable: false,
          type: RESPONSE_DATA_TYPES.PRIMITIVE,
        });
      }
      return;
    }

    // ПРИМИТИВ
    result.push({
      container,
      key,
      path: fullPath,
      value: value ?? "",
      isExpandable: false,
      type: RESPONSE_DATA_TYPES.PRIMITIVE,
    });
  });

  return result;
}

/**
 * Обрабатывает массив и его элементы
 * @private
 */
function handleArray(
  key,
  value,
  fullPath,
  container,
  result,
  locale,
  level,
  matchedMentions
) {
  // Пустой массив
  if (value.length === 0) {
    result.push({
      container,
      key,
      path: fullPath,
      value: "",
      isExpandable: true,
      type: RESPONSE_DATA_TYPES.ARRAY,
      children: [],
      childrenCount: 0,
    });
    return;
  }

  // Массив примитивов или справочников
  if (isSimpleArray(value)) {
    result.push({
      container,
      key,
      path: fullPath,
      value: normalizeSimpleArray(value, locale),
      isExpandable: false,
      type: RESPONSE_DATA_TYPES.PRIMITIVE,
    });
    return;
  }

  // Массив объектов
  const children = [];
  let selectedIndex = -1;

  value.forEach((item, index) => {
    // Проверяем совпадения с mentions для выбора элемента
    const itemMatches = matchedMentions.some((mention) =>
      JSON.stringify(item).includes(mention)
    );

    if (selectedIndex === -1 && itemMatches) {
      selectedIndex = index;
    }

    if (item && typeof item === "object") {
      const itemChildren = [];

      flattenToStructuredList(item, {
        locale,
        container: fullPath,
        result: itemChildren,
        pathPrefix: `${index}`,
        level: level + 1,
        isArrayItem: true,
        matchedMentions,
      });

      if (itemChildren.length > 0) {
        children.push({
          container: fullPath,
          key: `[${index}]`,
          path: `${fullPath}_${index}`,
          value: "",
          isExpandable: true,
          type: RESPONSE_DATA_TYPES.OBJECT,
          children: itemChildren,
          childrenCount: itemChildren.length,
        });
      }
    } else {
      children.push({
        container: fullPath,
        key: `[${index}]`,
        path: `${fullPath}_${index}`,
        value: item ?? "",
        isExpandable: false,
        type: RESPONSE_DATA_TYPES.PRIMITIVE,
      });
    }
  });

  result.push({
    container,
    key,
    path: fullPath,
    value: "",
    isExpandable: true,
    type: RESPONSE_DATA_TYPES.ARRAY,
    children,
    childrenCount: children.length,
    selectedIndex,
  });
}

/**
 * Главная функция для преобразования ответа сервиса
 * Автоматически выбирает между простым и структурированным вариантом
 * @param {Object} responseData - Данные от сервиса
 * @param {Object} options - Опции
 * @param {string} [options.locale] - Локаль
 * @param {boolean} [options.structured] - Создать структурированный список
 * @returns {Array} Преобразованный список полей
 */
export function flattenServiceResponse(
  responseData,
  { locale = LOCALES.RUSSIAN, structured = false } = {}
) {
  if (!responseData || typeof responseData !== "object") {
    return [];
  }

  if (structured) {
    return flattenToStructuredList(responseData, { locale });
  }

  return flattenToSimpleList(responseData, { locale });
}

export default flattenServiceResponse;
